import { useTranslate } from "@tolgee/react";
import { Info } from "lucide-react";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog";
import { Button } from "#/components/ui/button";
import { useHyperFees } from "#/hooks/use-hyper-fees";
import { formatCompactNumber } from "#/lib/formatters";

function formatRatePercent(rate: number) {
	return `${(rate * 100).toFixed(4)}%`;
}

function formatDiscountPercent(rate: number) {
	return `${(rate * 100).toFixed(2)}%`;
}

function formatNotionalCutoff(value: number, nextValue?: number) {
	if (value === 0 && nextValue) return `<= $${formatCompactNumber(nextValue)}`;
	if (value === 0) return "$0+";
	return `> $${formatCompactNumber(value)}`;
}

function StatusPill({
	label,
	active,
}: {
	label: string;
	active: boolean;
}) {
		return (
			<span
				className={
					active
						? "rounded-full border border-trade-green/50 bg-trade-green/10 px-1.5 sm:px-2.5 py-1 text-caption1-medium text-trade-green"
						: "rounded-full border border-border bg-muted/40 px-1.5 sm:px-2.5 py-1 text-caption1-medium text-muted-foreground"
				}
			>
				{label}
		</span>
	);
}

export function FeeOverview() {
	const { t } = useTranslate();
	const {
		fees,
		builderFeeRate,
		reportedHyperliquidTakerRate,
		reportedHyperliquidMakerRate,
		effectiveHyperliquidTakerRate,
		effectiveHyperliquidMakerRate,
		totalTakerRate,
		totalMakerRate,
		stakingDiscountRate,
		hasReferralDiscount,
		hasStakingDiscount,
		feeTierRows,
	} = useHyperFees();

	const totalRateLabel = `${formatRatePercent(totalTakerRate)} / ${formatRatePercent(totalMakerRate)}`;
	const reportedWithBuilderTaker = reportedHyperliquidTakerRate + builderFeeRate;
	const reportedWithBuilderMaker = reportedHyperliquidMakerRate + builderFeeRate;
	const effectiveWithBuilderTaker = effectiveHyperliquidTakerRate + builderFeeRate;
	const effectiveWithBuilderMaker = effectiveHyperliquidMakerRate + builderFeeRate;

	return (
		<div className="flex h-full flex-col justify-between">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
				<h2 className="text-body2-bold text-foreground">
					{t("portfolio.perps-fees")}
				</h2>

				<div className="flex flex-wrap items-center gap-2">
					<StatusPill
						label={
							hasReferralDiscount
								? t("portfolio.referral-pill")
								: t("portfolio.referral-inactive")
						}
						active={hasReferralDiscount}
					/>
					{hasStakingDiscount && (
						<StatusPill
							label={t("portfolio.staking-active", {
								discount: formatDiscountPercent(stakingDiscountRate),
							})}
							active
						/>
					)}
				</div>
			</div>


			<div>
				{fees && <p className="mt-5 sm:mt-0 pb-2 sm:pb-0 whitespace-nowrap text-title1 sm:text-display4 text-foreground">
					{totalRateLabel}
				</p>}


				<Dialog>
					<DialogTrigger asChild>
						<Button variant="ghost" size="sm" className="h-8 gap-1.5 !px-0">
							<Info className="h-3.5 w-3.5" />
							{t("portfolio.view-fee-details")}
						</Button>
					</DialogTrigger>
						<DialogContent className="w-[min(840px,calc(100vw-2rem))] rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 text-foreground shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)]">
							<div className="border-b border-trade-border-subtle px-5 py-4">
								<div className="flex items-start justify-between gap-4">
									<div>
										<DialogTitle className="text-title1 text-trade-text-strong">
											{t("portfolio.fee-schedule-title")}
										</DialogTitle>
										<DialogDescription className="mt-1 text-body2 text-trade-text-secondary">
											{t("portfolio.fee-schedule-description")}
										</DialogDescription>
									</div>
								<DialogCloseButton aria-label={t("actions.close")} />
							</div>
						</div>
						<div className="max-h-[calc(100dvh-10rem)] space-y-6 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
							<div className="grid gap-3 md:grid-cols-2">
								<DetailStat
									label={t("portfolio.fee-status-reported-hyperliquid")}
									value={`${formatRatePercent(reportedWithBuilderTaker)} / ${formatRatePercent(reportedWithBuilderMaker)}`}
								/>
								<DetailStat
									label={t("portfolio.fee-status-effective-hyperliquid")}
									value={`${formatRatePercent(effectiveWithBuilderTaker)} / ${formatRatePercent(effectiveWithBuilderMaker)}`}
								/>
							</div>

									<div className="overflow-x-auto scrollbar-none rounded-2xl border border-trade-border-subtle bg-trade-surface-inset">
										<table className="w-full min-w-[540px] text-left text-label2">
											<thead className="bg-trade-surface text-trade-text-secondary">
										<tr>
										<th className="px-3 py-2 text-label2-medium">{t("portfolio.fee-tier")}</th>
										<th className="px-3 py-2 text-label2-medium">{t("portfolio.fee-14d-volume")}</th>
										<th className="px-3 py-2 text-label2-medium">{t("portfolio.fee-perps-taker")}</th>
										<th className="px-3 py-2 text-label2-medium">{t("portfolio.fee-perps-maker")}</th>
									</tr>
									</thead>
										<tbody>
											{feeTierRows.map((row, index) => (
												<tr
													key={row.tier}
													className="border-t border-trade-border-subtle text-trade-text-strong"
												>
											<td className="px-3 py-2">{row.tier}</td>
											<td className="px-3 py-2">
												{formatNotionalCutoff(
													row.notionalCutoff,
													feeTierRows[index + 1]?.notionalCutoff,
												)}
											</td>
											<td className="px-3 py-2">{formatRatePercent(row.takerRate + builderFeeRate)}</td>
											<td className="px-3 py-2">{formatRatePercent(row.makerRate + builderFeeRate)}</td>
										</tr>
									))}
									</tbody>
								</table>
							</div>

						</div>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
}
function DetailStat({
	label,
	value,
}: {
	label: string;
	value: string;
}) {
	return (
		<div className="rounded-2xl border border-trade-border-subtle bg-trade-surface-inset p-3">
			<p className="text-caption1 uppercase text-trade-text-secondary">
				{label}
			</p>
			<p className="mt-2 text-headline text-trade-text-strong">{value}</p>
		</div>
	);
}
